/**
 * Drip — collection carousel (perf: only paint nearby slides, no mobile blur)
 */
(function () {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const HOVER = window.matchMedia('(hover: hover)');

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  class DripCollectionCarousel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-dcc-stage]');
      this.slides = Array.from(root.querySelectorAll('[data-dcc-slide]'));
      this.index = 0;
      this.dragging = false;
      this.listening = false;
      this.axis = null;
      this.startX = 0;
      this.startY = 0;
      this.deltaX = 0;
      this.pointerId = null;
      this.bound = false;
      this.raf = 0;
      this.timer = 0;
      this.inView = true;
      this.autoplay = root.dataset.autoplay !== 'false';
      this.interval = Math.max(2800, parseInt(root.dataset.autoplayMs || '4000', 10) || 4000);

      if (!this.stage || this.slides.length === 0) return;

      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onVisibility = this.onVisibility.bind(this);

      this.bind();
      this.render(true);
      this.startAutoplay();
    }

    destroy() {
      this.pauseAutoplay();
      this.removeDragListeners();
      if (this.io) this.io.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    bind() {
      if (this.bound) return;
      this.bound = true;
      this.stage.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      if (HOVER.matches) {
        this.stage.addEventListener('pointerenter', () => this.pauseAutoplay());
        this.stage.addEventListener('pointerleave', () => this.startAutoplay());
      }
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.stage.addEventListener('keydown', this.onKey);
      this.stage.setAttribute('tabindex', '0');
      this.stage.setAttribute('aria-roledescription', 'carousel');
      this.stage.style.touchAction = 'pan-y';

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.inView = entries.some((e) => e.isIntersecting && e.intersectionRatio > 0.15);
            if (this.inView) this.startAutoplay();
            else this.pauseAutoplay();
          },
          { threshold: [0, 0.15, 0.4] }
        );
        this.io.observe(this.root);
      }
    }

    startAutoplay() {
      this.pauseAutoplay();
      if (REDUCE || !this.autoplay || this.slides.length < 2) return;
      if (document.hidden || !this.inView) return;
      this.timer = window.setInterval(() => {
        if (this.dragging || this.axis === 'x') return;
        this.index += 1;
        this.render(false);
      }, this.interval);
    }

    pauseAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = 0;
      }
    }

    onVisibility() {
      if (document.hidden) this.pauseAutoplay();
      else this.startAutoplay();
    }

    addDragListeners() {
      if (this.listening) return;
      this.listening = true;
      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    removeDragListeners() {
      if (!this.listening) return;
      this.listening = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onResize() {
      if (this._resizeT) clearTimeout(this._resizeT);
      this._resizeT = setTimeout(() => this.render(true), 120);
    }

    onPointerDown(e) {
      if (e.button != null && e.button !== 0) return;
      this.pauseAutoplay();
      this.dragging = true;
      this.axis = null;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.deltaX = 0;
      this.pointerId = e.pointerId;
      this.stage.classList.remove('is-dragging');
      this.addDragListeners();
    }

    onPointerMove(e) {
      if (!this.dragging && this.axis !== 'x') return;

      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;

      if (this.axis == null) {
        if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return;

        if (Math.abs(dy) >= Math.abs(dx) * 0.85) {
          this.axis = 'y';
          this.dragging = false;
          this.deltaX = 0;
          this.stage.style.touchAction = 'pan-y';
          this.stage.classList.remove('is-dragging');
          this.removeDragListeners();
          this.render(false);
          this.startAutoplay();
          return;
        }

        this.axis = 'x';
        this.stage.classList.add('is-dragging');
        this.stage.style.touchAction = 'none';
        try {
          if (this.pointerId != null) this.stage.setPointerCapture(this.pointerId);
        } catch (_) {}
      }

      if (this.axis !== 'x') return;

      if (e.cancelable) e.preventDefault();
      this.deltaX = dx;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.render(false, this.deltaX / Math.max(window.innerWidth * 0.28, 80));
      });
    }

    onPointerUp() {
      const wasX = this.axis === 'x';
      this.dragging = false;
      this.stage.classList.remove('is-dragging');
      this.stage.style.touchAction = 'pan-y';
      this.removeDragListeners();

      try {
        if (this.pointerId != null) this.stage.releasePointerCapture(this.pointerId);
      } catch (_) {}

      if (wasX) {
        const threshold = Math.max(36, window.innerWidth * 0.08);
        if (this.deltaX > threshold) this.index -= 1;
        else if (this.deltaX < -threshold) this.index += 1;
      }

      this.deltaX = 0;
      this.axis = null;
      this.pointerId = null;
      this.render(false);
      this.startAutoplay();
    }

    onKey(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.pauseAutoplay();
        this.index -= 1;
        this.render(false);
        this.startAutoplay();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.pauseAutoplay();
        this.index += 1;
        this.render(false);
        this.startAutoplay();
      }
    }

    render(instant, dragOffset) {
      const n = this.slides.length;
      if (!n) return;
      const offset = dragOffset || 0;
      const isMobile = MOBILE.matches;
      const transition =
        instant || REDUCE || this.axis === 'x'
          ? 'none'
          : 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease';

      const xStep = isMobile ? 18 : 13;
      // Only paint a few neighbors — huge win with 30 collections
      const visibleRadius = isMobile ? 1.6 : 2.4;

      this.slides.forEach((slide, i) => {
        let d = i - this.index - offset;
        while (d > n / 2) d -= n;
        while (d < -n / 2) d += n;

        const abs = Math.abs(d);

        if (abs > visibleRadius + 0.2) {
          slide.style.visibility = 'hidden';
          slide.style.opacity = '0';
          slide.style.pointerEvents = 'none';
          slide.style.willChange = 'auto';
          slide.style.filter = 'none';
          slide.classList.remove('is-active');
          return;
        }

        slide.style.visibility = 'visible';

        const x = d * xStep;
        const rotY = isMobile ? 0 : clamp(d * -10, -22, 22);
        let z = -Math.min(abs, 3) * (isMobile ? 90 : 150);
        let scale = clamp(1 - abs * (isMobile ? 0.12 : 0.1), 0.62, 1);
        let opacity = clamp(1 - abs * 0.22, 0.35, 1);
        let zIndex = Math.round(20 - abs * 3);
        // Skip CSS filter blur everywhere — opacity/scale is enough (desktop blur = major jank)
        const blur = 0;

        const active = Math.round(d) === 0 && this.axis !== 'x';

        slide.style.transition = transition;
        slide.style.transform = isMobile
          ? `translate3d(calc(-50% + ${x}vw), -50%, ${z}px) scale(${scale})`
          : `translate3d(calc(-50% + ${x}vw), -50%, ${z}px) rotateY(${rotY}deg) scale(${scale})`;
        slide.style.opacity = String(opacity);
        slide.style.zIndex = String(zIndex);
        slide.style.filter = 'none';
        slide.style.pointerEvents = Math.round(d) === 0 ? 'auto' : 'none';
        slide.style.willChange = abs < 1.2 ? 'transform, opacity' : 'auto';
        slide.classList.toggle('is-active', active);
      });
    }
  }

  function init() {
    document.querySelectorAll('.drip-col-carousel').forEach((el) => {
      if (el.classList.contains('section-background')) return;
      if (el.__dcc) {
        el.__dcc.destroy?.();
        el.__dcc = null;
      }
      el.__dcc = new DripCollectionCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
